import { GridColDef, GridRowParams } from '@mui/x-data-grid-pro';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { Box, IconButton, Menu, MenuItem, styled } from '@mui/material';
import CommonEntityStateChip from '../shared/CommonEntityStateChip';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';
import { DataGridPro } from '../../tables/DataGridPro';
import { useState } from 'react';
import {
  OperandType,
  TransportationBenefitModel,
  TransportationBenefitModelState,
} from '../../../data/models/transportation-benefit-model.model';
import { MoreVert } from '@mui/icons-material';
import { hasWordStartingWith } from '../../../utils/string.utils';

const PREFIX = 'TransportationBenefitModelList';

const classes = {
  moreButtonContainer: `${PREFIX}-moreButtonContainer`,
  moreButton: `${PREFIX}-moreButton`,
  hoveredRow: `${PREFIX}-hoveredRow`,
};

const StyledDataGridPro = styled(DataGridPro)(({ theme }) => ({
  cursor: 'pointer',
  [`& .${classes.moreButtonContainer}`]: {
    display: 'flex',
    justifyContent: 'end',
    width: '100%',
  },
  [`& .${classes.moreButton}`]: {
    marginRight: theme.spacing(1),
  },
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    minWidth: '170px',
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  ...theme.typography.body2,
}));

export enum TransportationBenefitModelMenuItem {
  Delete = 'Delete',
  Active = 'Active',
  Inactive = 'Inactive',
}

interface TransportationBenefitModelListProps {
  data: TransportationBenefitModel[];
  searchText: string;
  handleMenuItemClick: (
    menuItem: TransportationBenefitModelMenuItem,
    transportationBenefitModelUUID: string,
  ) => void;
  handleRowClick: (transportationBenefitModelUUID: string) => void;
}

interface TransportationBenefitModelListItem
  extends TransportationBenefitModel {
  type: string;
}

export default function TransportationBenefitModelList({
  data,
  searchText,
  handleMenuItemClick,
  handleRowClick: handleRowClickProp,
}: TransportationBenefitModelListProps) {
  const t = useCommonTranslation();
  const isSuperReaderUser = useIsSuperReaderUser();
  const [hoveredRow, setHoveredRow] = useState<string | undefined>(undefined);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedTransBenefitModelUUID, setSelectedTransBenefitModelUUID] =
    useState<string | null>(null);

  const getType = (model: TransportationBenefitModel) => {
    const expression = model.expression;
    if (Object.keys(expression).includes('literal')) {
      return t(`transBenefitModel.${OperandType.literal}`);
    } else if (Object.keys(expression).includes('function')) {
      return t(`transBenefitModel.${OperandType.function}`);
    } else if (Object.keys(expression).includes('operator')) {
      return t(`transBenefitModel.${OperandType.nested}`);
    }

    return '';
  };

  const rowItems: TransportationBenefitModelListItem[] = data
    .map((data) => ({
      ...data,
      type: getType(data),
    }))
    .filter(
      (rowItem) =>
        hasWordStartingWith(rowItem.name, searchText) ||
        hasWordStartingWith(rowItem.type, searchText),
    );

  const selectedTransBenefitModelState = rowItems.find(
    (rowItem) =>
      rowItem.transportationBenefitModelUUID === selectedTransBenefitModelUUID,
  )?.state;

  const handleRowClick = (params: GridRowParams) => {
    handleRowClickProp(params.row.transportationBenefitModelUUID);
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    transportationBenefitModelUUID: string,
  ) => {
    event.stopPropagation();
    setSelectedTransBenefitModelUUID(transportationBenefitModelUUID);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setHoveredRow(undefined);
  };

  const handleSetActiveClick = () => {
    if (selectedTransBenefitModelUUID) {
      handleMenuItemClick(
        TransportationBenefitModelMenuItem.Active,
        selectedTransBenefitModelUUID,
      );
    }
    handleMenuClose();
  };

  const handleSetInactiveClick = () => {
    if (selectedTransBenefitModelUUID) {
      handleMenuItemClick(
        TransportationBenefitModelMenuItem.Inactive,
        selectedTransBenefitModelUUID,
      );
    }
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    if (selectedTransBenefitModelUUID) {
      handleMenuItemClick(
        TransportationBenefitModelMenuItem.Delete,
        selectedTransBenefitModelUUID,
      );
    }
    handleMenuClose();
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('transBenefitModel.transBenefitModelName'),
      sortable: false,
      minWidth: 400,
    },
    {
      field: 'type',
      headerName: t('transBenefitModel.type'),
      sortable: false,
      minWidth: 180,
    },
    {
      field: 'state',
      headerName: t('transBenefitModel.status'),
      sortable: false,
      minWidth: 87,
      renderCell: (params) => (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="100%">
          <CommonEntityStateChip state={params.row.state} />
        </Box>
      ),
    },
    {
      field: 'action',
      headerName: '',
      sortable: false,
      flex: 1,
      renderCell: (params) => {
        if (isSuperReaderUser) {
          return null;
        }
        if (params.row.transportationBenefitModelUUID === hoveredRow) {
          return (
            <Box className={classes.moreButtonContainer}>
              <IconButton
                className={classes.moreButton}
                onClick={(event) =>
                  handleMenuClick(
                    event,
                    params.row.transportationBenefitModelUUID,
                  )
                }>
                <MoreVert />
              </IconButton>
            </Box>
          );
        }
      },
    },
  ];

  return (
    <>
      <StyledDataGridPro
        disableColumnMenu={true}
        getRowId={(row) => row.transportationBenefitModelUUID}
        rows={rowItems ?? []}
        columns={columns}
        rowCount={rowItems?.length}
        pagination={true}
        autoHeight
        onRowClick={handleRowClick}
        slotProps={{
          row: {
            onMouseEnter: (event) => {
              const id = event.currentTarget.dataset.id;
              setHoveredRow(id);
            },
          },
        }}
      />
      <StyledMenu
        anchorEl={menuAnchorEl}
        open={!!menuAnchorEl}
        onClose={handleMenuClose}
        transformOrigin={{
          vertical: 'top',
          horizontal: 120,
        }}>
        {(selectedTransBenefitModelState ===
          TransportationBenefitModelState.draft ||
          selectedTransBenefitModelState ===
            TransportationBenefitModelState.inactive) && (
          <StyledMenuItem onClick={() => handleSetActiveClick()}>
            {t('transBenefitModel.setActive')}
          </StyledMenuItem>
        )}
        {selectedTransBenefitModelState ===
          TransportationBenefitModelState.active && (
          <StyledMenuItem onClick={() => handleSetInactiveClick()}>
            {t('transBenefitModel.setInactive')}
          </StyledMenuItem>
        )}
        {(selectedTransBenefitModelState ===
          TransportationBenefitModelState.draft ||
          selectedTransBenefitModelState ===
            TransportationBenefitModelState.inactive) && (
          <StyledMenuItem onClick={() => handleDeleteClick()}>
            {t('transBenefitModel.delete')}
          </StyledMenuItem>
        )}
      </StyledMenu>
    </>
  );
}
